/**
 * PROJECT-STEP-CHECKS.UTILS
 * Pure rules of the steps of a project (message of welcome, SMS), used by the checks of the project
 */

interface MessageOptionsLike {
  title?: { [language: string]: string }
  content?: { [language: string]: string }
  infoContent?: { [language: string]: string }
  isInfoCustom?: boolean
  isWelcomeHidden?: boolean
  isConfirmationSend?: boolean
  confirmationTitle?: { [language: string]: string }
  confirmationContent?: { [language: string]: string }
  confirmationSender?: { [language: string]: string }
  isSummaryAvailable?: boolean
  summaryContent?: { [language: string]: string }
}

const isEmpty = (values: { [language: string]: string } | undefined, language: string): boolean =>
  !values || !values[language] || values[language].trim().length < 1

//First language in which one of the fields is empty (the language to open to fix it), undefined when none is missing
export function getFirstMissingLanguage(languages: string[], ...fields: ({ [language: string]: string } | undefined)[]): string | undefined {
  return languages.find((language) => fields.some((field) => isEmpty(field, language)))
}

//Keys of the warnings of the message step. Several languages : the key says "in every language"
export function getMessageWarnings(languages: string[], options: MessageOptionsLike): string[] {
  const warnings: string[] = []
  const several = languages.length > 1
  const missing = (field: keyof MessageOptionsLike) => languages.some((language) => isEmpty(options[field] as any, language))

  //The welcome message is not displayed when it is hidden : its title and text do not matter
  if (!options.isWelcomeHidden) {
    if (missing("title")) warnings.push(several ? "project_warning_message_missing_title_language" : "project_warning_message_missing_title")
    if (missing("content")) {
      warnings.push(several ? "project_warning_message_missing_content_language" : "project_warning_message_missing_content")
    }
  }

  if (options.isInfoCustom && missing("infoContent")) {
    warnings.push(several ? "project_warning_message_missing_info_language" : "project_warning_message_missing_info")
  }

  //E-mail sent to the participant at the end of the survey
  if (options.isConfirmationSend && (missing("confirmationTitle") || missing("confirmationContent") || missing("confirmationSender"))) {
    warnings.push("project_warning_message_missing_confirmation")
  }

  //Summary offered at the end of the survey
  if (options.isSummaryAvailable && missing("summaryContent")) {
    warnings.push("project_warning_message_missing_summary")
  }

  return warnings
}

//Name of the sender of the SMS : operators refuse a sender of more than 11 characters or with accents and special characters
export function isSmsSenderValid(sender: string | null | undefined): boolean {
  const value = (sender || "").trim()
  return /^[A-Za-z0-9 -]{3,11}$/.test(value)
}

//Languages of a project : French comes first whatever the order of the choices (the first one is the main language : it writes the labels of the alerts)
export function sortProjectLanguages(languages: string[]): string[] {
  return languages.includes("fr") ? ["fr", ...languages.filter((language) => language !== "fr")] : languages
}
